import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { ThemeProvider, createTheme, CssBaseline, Box } from "@mui/material";
import { LandingLayout } from "@coremind/site-template-landing";
import { WorkspacesProvider, WorkspaceSwitcherMenu } from "@coremind/workspaces-core";
import { NotificationsProvider, NotificationsBell } from "@coremind/notifications-core";
const wwwTheme = createTheme({
    palette: {
        mode: "dark",
        background: { default: "#0a0d14", paper: "#10141f" },
        primary: { main: "#6366f1", light: "#818cf8", dark: "#4f46e5", contrastText: "#fff" },
        secondary: { main: "#06b6d4" },
        divider: "rgba(255,255,255,0.07)",
        text: { primary: "#f1f5f9", secondary: "#94a3b8" },
    },
    typography: { fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif' },
    shape: { borderRadius: 10 },
    components: {
        MuiCard: { styleOverrides: { root: { background: "#10141f", border: "1px solid rgba(255,255,255,0.07)" } } },
        MuiPaper: { styleOverrides: { outlined: { borderColor: "rgba(255,255,255,0.07)" } } },
    },
});
// Publish to window.__COREMIND_THEME__ -- same global every federated pack's own
// CoremindThemeProvider reads (inventory-ui, calendar-ui, etc., per each pack's own
// coremindTheme.ts: prop > window.__COREMIND_THEME__ > default). www never did this before
// (only dashboard-template sites' own ThemeBridge did, via publishThemeToWindow) since www has
// no per-workspace theme editor -- but any federated component it mounts still needs to inherit
// this site's real theme instead of silently falling back to MUI's own light default. Found live
// 2026-09-09: the calendar-ui panels on /app and /app/calendar-hub rendered white instead of
// www's actual dark theme until this was added.
window.__COREMIND_THEME__ = wwwTheme;
// LifeMind/WorkMind/ArenaMind/Pricing/Docs/Status dropped from the top bar -- by direct
// instruction, these become marketplace-addable sites later rather than fixed nav links. Their
// routes/pages stay in adapter.tsx for now (still reachable directly, footer still links
// Docs/Status) since it isn't decided yet whether they'll have standalone pages at all.
const NAV_ITEMS = [];
// Replaces the shared template's DEFAULT_FOOTER, whose Privacy/Terms/Docs/Status links were all
// hardcoded to "#" -- all four now have real in-app routes (see adapter.tsx).
const Footer = (_jsxs(Box, { component: "footer", sx: {
        borderTop: "1px solid",
        borderColor: "divider",
        py: 3,
        px: 4,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        bgcolor: "background.paper",
    }, children: [_jsxs(Box, { sx: { color: "text.secondary", fontSize: 13 }, children: ["\u00A9 ", new Date().getFullYear(), " Offsetmind. All rights reserved."] }), _jsx(Box, { sx: { display: "flex", gap: 3 }, children: [
                { label: "Privacy", href: "/privacy" },
                { label: "Terms", href: "/terms" },
                { label: "Docs", href: "/docs" },
                { label: "Status", href: "/status" },
            ].map(({ label, href }) => (_jsx(Box, { component: RouterLink, to: href, sx: { color: "text.secondary", fontSize: 13, textDecoration: "none", "&:hover": { color: "text.primary" } }, children: label }, label))) })] }));
const Logo = (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Box, { component: "img", src: "images/logo.png", alt: "Offsetmind", sx: { width: 26, height: 26, flexShrink: 0 } }), _jsx(Box, { sx: { fontWeight: 700, fontSize: 17, letterSpacing: "-0.3px", color: "text.primary" }, children: "Offsetmind" })] }));
/** Workspace switcher + notification bell in the navbar -- the same shell chrome every
 * dashboard-style site already inherits (see templates/dashboard/src/Layout.tsx). Both
 * components no-op gracefully when signed out, so they're always mounted rather than gated
 * on session -- avoids a header layout jump the instant a visitor signs in. */
function NavbarActions() {
    const navigate = useNavigate();
    return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx(WorkspaceSwitcherMenu, { manageHref: "/workspaces", onNavigate: navigate, iconColor: "inherit" }), _jsx(NotificationsBell, { color: "inherit" })] }));
}
// First path segment of every *static* top-level route in adapter.tsx. foundation-shell-core's
// SiteAdapter has exactly one `layout` for the whole site (no per-route override -- see its own
// index.d.ts), so a route that needs to skip this site's OffsetMind navbar/footer chrome has to
// be detected here instead. Today that's just the public sable Assistant chat page
// (mini-brain-business-card-sites.2026-10-07 proposal §6 item 1 / §7.1): it's a public,
// owner-branded, mobile-first page meant to read as sable's own product, not wrapped in a second,
// unrelated dark OffsetMind shell -- same reasoning sable's own Layout.tsx avoids the platform's
// generic dashboard template for (see that proposal's §7.3/§8.1). react-router already ranks
// every static route above the dynamic `/:slug` catch-all, so "first segment isn't one of these"
// is exactly the same test the router itself uses to decide a path is a slug. Keep this set in
// sync with adapter.tsx's own route list if a new static top-level route is ever added.
const RESERVED_TOP_LEVEL_SEGMENTS = new Set([
    "signin",
    "auth",
    "contacts",
    "app",
    "workflows",
    "profile",
    "workspaces",
    "lifemind",
    "workmind",
    "arenamind",
    "privacy",
    "terms",
    "legal",
    "pricing",
    "docs",
    "status",
]);
export default function WwwLayout({ children }) {
    const location = useLocation();
    const firstSegment = location.pathname.split("/")[1] ?? "";
    const isAssistantChatRoute = firstSegment !== "" && !RESERVED_TOP_LEVEL_SEGMENTS.has(firstSegment);
    if (isAssistantChatRoute) {
        // Render bare: no MUI theme/CssBaseline, no WorkspacesProvider/NotificationsProvider. This is
        // an anonymous public page with no session -- mounting those providers anyway would fire real
        // network calls a visitor's browser has no business making (the same kind of needless-network-
        // call finding sable's own build hit with @coremind/billing-core's BillingProvider, proposal
        // §8.1's "real findings" section). AssistantChatPage.tsx brings its own CSS/reset.
        return _jsx(_Fragment, { children: children });
    }
    return (_jsxs(ThemeProvider, { theme: wwwTheme, children: [_jsx(CssBaseline, {}), _jsx(WorkspacesProvider, { children: _jsx(NotificationsProvider, { children: _jsx(LandingLayout, { navItems: NAV_ITEMS, slots: { logo: Logo, navbarActions: _jsx(NavbarActions, {}), footer: Footer }, children: children }) }) })] }));
}
